import { AddressZero, MaxUint256 } from '@ethersproject/constants';
import { formatEther, parseEther } from '@ethersproject/units';
import { useWeb3React } from '@web3-react/core';
import { Contract } from 'ethers';
import React, { useContext, useState } from 'react';
import NavLogo from '../../assets/logos/SpoolLogo';
import Button from '../../components/Button';
import CustomModal from '../../components/CustomModal';
import CustomModalHeader from '../../components/CustomModal/CustomModalHeader';
import TextInput from '../../components/TextInput';
import abis from '../../config/abis';
import {
    APPROVAL_PENDING,
    INSUFFICIENT_FUNDS_ERROR,
    STAKE_CONFIRMED,
    STAKE_PENDING,
} from '../../config/constants';
import appContext from '../../context/app/appContext';
import errorContext from '../../context/error/errorContext';
import modalContext from '../../context/modal/modalContext';
import { formatAccount, formatUSD } from '../../helpers/formats';
import useIsApproved from '../../hooks/web3/useIsApproved';
import useUserBalance from '../../hooks/web3/useUserBalance';
import useUserStakedSpool from '../../hooks/web3/vault/useUserStakedSpool';
import { calculateMargin, preventOverflow, sleep, toBigNumber } from '../../web3/utils';

const StakeModal: React.FC = () => {
    const { account, library } = useWeb3React();
    const { setError } = useContext(errorContext);
    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const { userStakedSpool, updateUserStakedSpool } = useUserStakedSpool();

    const { interfaceSDK, chainConfig } = useContext(appContext);

    const [amount, setAmount] = useState('');

    const userBalance = useUserBalance(chainConfig.spoolToken);
    const { isApproved, mutate } = useIsApproved(
        chainConfig.spoolToken,
        chainConfig.staking,
        amount
    );

    const onClose = () => {
        closeModal('stakeModal');
        setAmount('');
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setAmount(preventOverflow(value));
    };

    const handleMaxInput = () => {
        if (!userBalance) return;

        setAmount(formatEther(userBalance));
    };

    const approve = async () => {
        try {
            if (!library) return;

            setModalData((prev) => ({ ...prev, message: APPROVAL_PENDING }));
            openModal('waitingModal');

            const contract = new Contract(
                chainConfig.spoolToken,
                abis.erc20,
                library.getSigner()
            );

            let useExact = false;

            const estimatedGas = await contract.estimateGas
                .approve(chainConfig.staking, MaxUint256)
                .catch(() => {
                    // general fallback for tokens who restrict approval amounts
                    useExact = true;

                    return contract.estimateGas.approve(
                        chainConfig.staking,
                        parseEther(amount)
                    );
                });

            const approve = await contract.approve(
                chainConfig.staking,
                useExact ? parseEther(amount) : MaxUint256,
                {
                    gasLimit: calculateMargin(estimatedGas),
                }
            );

            await approve.wait();

            await mutate();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    const stake = async () => {
        try {
            if (!interfaceSDK || !userStakedSpool || !userBalance) return;

            if (userBalance.lt(toBigNumber(amount))) {
                openModal('errorModal');
                setModalData((prev) => ({
                    ...prev,
                    message: INSUFFICIENT_FUNDS_ERROR,
                }));
                return;
            }

            setModalData((prev) => ({ ...prev, message: STAKE_PENDING }));
            openModal('waitingModal');

            const stakingContract =
                await interfaceSDK.get.getSpoolStakingContractInstance();

            const stake = await stakingContract.stake(parseEther(amount));

            const receipt = await stake.wait();

            await sleep(5000);

            await updateUserStakedSpool(userStakedSpool);

            onClose();
            setModalData((prev) => ({
                ...prev,
                status: STAKE_CONFIRMED,
                txHash: receipt.transactionHash,
                message: '',
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <CustomModal modalName='stakeModal' className='stake-modal'>
            <CustomModalHeader title='Stake' onClose={onClose} />
            <div className='content'>
                <div className='staking-detail'>
                    <div className='field-container from'>
                        <span className='field'>From</span>
                        <span>{account ? formatAccount(account) : 'User Address'}</span>
                    </div>
                    <div className='field-container balance'>
                        <span className='field'>$SPOOL in Wallet</span>
                        <span>
                            {formatUSD(userBalance ? formatEther(userBalance) : 0)}
                        </span>
                    </div>
                    <div className='field-container input'>
                        <span className='field'>Input</span>
                        <div className='input-container'>
                            <TextInput
                                onChange={handleChange}
                                value={amount}
                                name='amount'
                                placeholder='0.00'
                            />
                            <Button onClick={handleMaxInput} variant='secondary'>
                                MAX
                            </Button>
                        </div>
                    </div>
                    <div className='field-container token'>
                        <div className='logo'>
                            <NavLogo />
                        </div>
                        <span className='token-name'>$SPOOL</span>
                    </div>
                    <div className='field-container to'>
                        <span className='field'>To</span>
                        <span>$SPOOL Staking Vault</span>
                        <span className='address'>
                            {formatAccount(chainConfig.staking || AddressZero)}
                        </span>
                    </div>
                    <div className='field-container staked'>
                        <span className='field'>Staked</span>
                        <span>{formatUSD(userStakedSpool || 0)}</span>
                    </div>
                </div>

                {isApproved ? (
                    <Button
                        onClick={stake}
                        disabled={!amount || parseEther(amount).isZero()}
                    >
                        Stake
                    </Button>
                ) : (
                    <Button onClick={approve}>Approve</Button>
                )}
            </div>
        </CustomModal>
    );
};

export default StakeModal;
